/**
 * Which writes may be replayed automatically after a gateway timeout.
 *
 * Idempotency in the API is per route, not global: some routes dedupe on the
 * Idempotency-Key at the repository, others do not. Replaying a write that does
 * not dedupe could create it twice, so replay is an explicit allowlist of
 * routes verified in `apps/api` to return the stored result for a repeated key
 * (or to be pure computations with no write at all). Everything else shows
 * "may have completed, check before repeating" and is never re-sent for the
 * person.
 *
 * Verified in apps/api (method POST):
 *   /v1/calculations/import                    deterministic id + ON CONFLICT(id) + request hash (saveLotUpload)
 *   /v1/calculations/simulate                  reserveCalculation idempotency, replay returns stored response
 *   /v1/calculations/uploads/:id/tasks         createFiscalTask beginIdempotency
 *   /v1/calculations/uploads/:id/input-corrections  createLotInputCorrection beginIdempotency
 *   /v1/fiscal-tasks                           createFiscalTask beginIdempotency
 *   /v1/calculator-research/requests           createCalculatorResearch beginIdempotency (single and ::batch share create())
 *   /v1/calculator-research/requests::batch    same
 *   /v1/nfe-imports                            importNfeUploadRows beginIdempotency
 * Pure computations, nothing is written, so repeating is always safe:
 *   /v1/calculations/inspect, /v1/calculations/repair-suggestions, /v1/calculations/coverage, /v1/calculator-research/requests::estimate
 *
 * Deliberately NOT replayed: rule-proposals::import-plan (the route takes no key), recalculate,
 * cancel/retry, evidence tasks, rule proposals and decisions, integration keys, passkeys, publish.
 */
const REPLAY_SAFE_PATHS: readonly RegExp[] = [
  /^\/v1\/calculations\/import$/,
  /^\/v1\/calculations\/simulate$/,
  /^\/v1\/calculations\/uploads\/[^/]+\/tasks$/,
  /^\/v1\/calculations\/uploads\/[^/]+\/input-corrections$/,
  /^\/v1\/fiscal-tasks$/,
  /^\/v1\/calculator-research\/requests$/,
  /^\/v1\/calculator-research\/requests::batch$/,
  /^\/v1\/nfe-imports$/,
];

const PURE_COMPUTE_PATHS: readonly RegExp[] = [
  /^\/v1\/calculations\/inspect$/,
  /^\/v1\/calculations\/repair-suggestions$/,
  /^\/v1\/calculations\/coverage$/,
  /^\/v1\/calculator-research\/requests::estimate$/,
];

function pathOnly(path: string): string {
  return path.split("?")[0] ?? path;
}

/** True when the API returns the stored result for a repeated Idempotency-Key on this route. */
export function isIdempotentReplayAllowed(method: string, path: string): boolean {
  if (method !== "POST") return false;
  const target = pathOnly(path);
  return [...REPLAY_SAFE_PATHS, ...PURE_COMPUTE_PATHS].some((pattern) => pattern.test(target));
}

/** A POST that only computes: it cannot have "committed" anything. */
export function isPureCompute(path: string): boolean {
  const target = pathOnly(path);
  return PURE_COMPUTE_PATHS.some((pattern) => pattern.test(target));
}
